import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { downloadFile } from './utils/downloadUtils';

const JobsTableModal = ({ 
  isOpen, 
  onClose, 
  jobs, 
  title, 
  mode = 'optimized', // 'optimized', 'unoptimized', or 'dependency'
  downloadLinks = null,
  optimizationResults = null 
}) => {
  const [tableWidth, setTableWidth] = useState(2000);

  if (!isOpen) return null;

  // Generate CSV from jobs data (matching batch CSV format)
  const generateCSVFromJobs = () => {
    if (!jobs || jobs.length === 0) return '';
    
    // CSV header matching the batch CSV format exactly
    const headers = [
      'position', 'job', 'suffix', 'oper_num', 'work_center','color', 'process_width', 
      'guideline', 'operation_type', 'temperature', 'enddate', 'dependencies',
      'shift_number', 'start_time', 'end_time', 'shift_start_time', 'shift_end_time',
      'run_time', 'actual_setup_time', 'setup_reason'
    ];
    
    // Build CSV rows
    const rows = jobs.map((job, index) => {
      const values = [
        index + 1, // position
        job.job || '',
        job.suffix || '',
        job.oper_num || job.operNum || '',
        job.work_center || job.workCenter || '',
        job.color && job.color !== 'NO_COLOR' ? job.color : '',
        job.process_width || job.processWidth || '',
        job.guideline || '',
        job.operation_type || job.operationType || '',
        job.temperature || '',
        job.enddate || job.dueDate || '',
        job.dependencies || '',
        job.shift_number !== undefined ? job.shift_number : (job.shiftNumber !== undefined ? job.shiftNumber : ''),
        job.start_time !== undefined ? job.start_time.toFixed(2) : (job.startTime !== undefined ? job.startTime.toFixed(2) : ''),
        job.end_time !== undefined ? job.end_time.toFixed(2) : (job.endTime !== undefined ? job.endTime.toFixed(2) : ''),
        job.shift_start_time !== undefined ? job.shift_start_time.toFixed(2) : (job.shiftStartTime !== undefined ? job.shiftStartTime.toFixed(2) : ''),
        job.shift_end_time !== undefined ? job.shift_end_time.toFixed(2) : (job.shiftEndTime !== undefined ? job.shiftEndTime.toFixed(2) : ''),
        job.run_time !== undefined ? job.run_time.toFixed(2) : (job.runTime !== undefined ? job.runTime.toFixed(2) : ''),
        job.actual_setup_time !== undefined ? job.actual_setup_time.toFixed(2) : (job.actualSetupTime !== undefined ? job.actualSetupTime.toFixed(2) : (job.setup_time !== undefined ? job.setup_time.toFixed(2) : '')),
        job.setup_reason || job.setupReason || ''
      ];
      
      // Escape values that contain commas or quotes
      return values.map(val => {
        const strVal = String(val);
        if (strVal.includes(',') || strVal.includes('"') || strVal.includes('\n')) {
          return `"${strVal.replace(/"/g, '""')}"`;
        }
        return strVal;
      }).join(',');
    });
    
    return [headers.join(','), ...rows].join('\n');
  };

  const downloadCSVFromJobs = () => {
    const csv = generateCSVFromJobs();
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const filename = `${title.replace(/[^a-zA-Z0-9]/g, '_')}_${timestamp}.csv`;
    
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const formatTimeForDisplay = (hours) => {
    if (hours < 1) {
      return `${Math.round(hours * 60)}m`;
    }
    return `${hours.toFixed(1)}h`;
  };

  const calculateDuration = (startTime, endTime) => {
    const duration = endTime - startTime;
    return formatTimeForDisplay(duration);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    // Check if it's an Excel serial date (number)
    if (typeof dateString === 'number') {
      // Excel dates are days since January 1, 1900
      const excelEpoch = new Date(1900, 0, 1);
      const daysOffset = dateString - 2; // Excel has a leap year bug for 1900
      const date = new Date(excelEpoch.getTime() + daysOffset * 24 * 60 * 60 * 1000);
      return date.toLocaleDateString('en-US');
    }
    
    // Otherwise treat as regular date string
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US');
  };

  const getStatusBadgeStyle = (status) => {
    const baseStyle = {
      padding: '4px 10px',
      borderRadius: '12px',
      fontSize: '11px',
      fontWeight: '600',
      display: 'inline-block'
    };

    switch(status) {
      case 'completed':
        return { ...baseStyle, background: '#d4edda', color: '#155724' };
      case 'running':
        return { ...baseStyle, background: '#fff3cd', color: '#856404' };
      case 'pending':
        return { ...baseStyle, background: '#f8d7da', color: '#721c24' };
      default:
        return { ...baseStyle, background: '#e2e3e5', color: '#383d41' };
    }
  };

  return createPortal(
    <div 
      className="job-modal-overlay" 
      onClick={onClose} 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        backdropFilter: 'blur(2px)'
      }}
    >
      <div 
        className="job-modal" 
        onClick={(e) => e.stopPropagation()} 
        style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          maxWidth: `${tableWidth}px`,
          width: '90%',
          maxHeight: '85vh',
          overflow: 'auto',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          resize: 'horizontal',
          minWidth: '800px'
        }}
      >
        {/* Header */}
        <div 
          className="job-modal-header" 
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
            paddingBottom: '12px',
            borderBottom: '2px solid #667eea'
          }}
        >
          <h3 style={{ 
            margin: 0, 
            fontSize: '20px',
            color: '#2c3e50',
            fontWeight: '600'
          }}>
            {title}
          </h3>
          <button 
            className="job-modal-close" 
            onClick={onClose} 
            title="Close"
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#95a5a6',
              padding: '0 8px',
              transition: 'color 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.color = '#e74c3c'}
            onMouseLeave={(e) => e.target.style.color = '#95a5a6'}
          >
            ✕
          </button>
        </div>
        
        {/* Body */}
        <div className="job-modal-body">
          {jobs.length > 0 ? (
            <>
              {/* Summary Section - Optimized Mode */}
              {mode === 'optimized' && optimizationResults && (
                <div 
                  className="job-modal-summary" 
                  style={{
                    display: 'flex',
                    gap: '20px',
                    marginBottom: '20px',
                    padding: '16px',
                    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                    borderRadius: '8px',
                    fontSize: '13px',
                    flexWrap: 'wrap',
                    alignItems: 'center'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '18px' }}>📊</span>
                    <div>
                      <div style={{ fontSize: '11px', color: '#7f8c8d', marginBottom: '2px' }}>Total Jobs</div>
                      <div style={{ fontSize: '16px', fontWeight: '700', color: '#2c3e50' }}>{jobs.length}</div>
                    </div>
                  </div>
                  
                  <div style={{ width: '1px', height: '40px', background: 'rgba(0,0,0,0.1)' }} />
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '18px' }}>🔄</span>
                    <div>
                      <div style={{ fontSize: '11px', color: '#7f8c8d', marginBottom: '2px' }}>Shifts Used</div>
                      <div style={{ fontSize: '16px', fontWeight: '700', color: '#2c3e50' }}>
                        {optimizationResults.shifts_used || 'N/A'}
                      </div>
                    </div>
                  </div>
                  
                  <div style={{ width: '1px', height: '40px', background: 'rgba(0,0,0,0.1)' }} />
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '18px' }}>⏱️</span>
                    <div>
                      <div style={{ fontSize: '11px', color: '#7f8c8d', marginBottom: '2px' }}>Total Time</div>
                      <div style={{ fontSize: '16px', fontWeight: '700', color: '#2c3e50' }}>
                        {jobs.length > 0 
                          ? Math.max(...jobs.map(job => job.end_time)).toFixed(1)
                          : (optimizationResults.completion_time?.toFixed(1) || 'N/A')}h
                      </div>
                    </div>
                  </div>
                  
                  <div style={{ width: '1px', height: '40px', background: 'rgba(0,0,0,0.1)' }} />
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '18px' }}>⚡</span>
                    <div>
                      <div style={{ fontSize: '11px', color: '#7f8c8d', marginBottom: '2px' }}>Solve Time</div>
                      <div style={{ fontSize: '16px', fontWeight: '700', color: '#2c3e50' }}>
                        {optimizationResults.solve_time?.toFixed(2) || 'N/A'}s
                      </div>
                    </div>
                  </div>
                  
                  {downloadLinks && (
                    <>
                      <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button
                          onClick={() => downloadFile(downloadLinks.txt, downloadLinks.txtName)}
                          style={{
                            padding: '8px 16px',
                            background: '#667eea',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = '#5a6fd8';
                            e.target.style.transform = 'translateY(-1px)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = '#667eea';
                            e.target.style.transform = 'translateY(0)';
                          }}
                        >
                          📄 Download TXT
                        </button>
                        <button
                          onClick={downloadCSVFromJobs}
                          style={{
                            padding: '8px 16px',
                            background: '#27ae60',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = '#229954';
                            e.target.style.transform = 'translateY(-1px)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = '#27ae60';
                            e.target.style.transform = 'translateY(0)';
                          }}
                        >
                          📊 Download CSV
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
              
              {/* Summary Section - Unoptimized Mode */}
              {mode === 'unoptimized' && (
                <div 
                  className="job-modal-summary" 
                  style={{
                    display: 'flex',
                    gap: '16px',
                    marginBottom: '20px',
                    padding: '16px',
                    background: 'linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%)',
                    borderRadius: '8px',
                    fontSize: '13px',
                    alignItems: 'center'
                  }}
                >
                  <span style={{ fontSize: '24px' }}>⏳</span>
                  <div>
                    <div style={{ fontSize: '11px', color: '#2d3436', marginBottom: '2px', opacity: 0.8 }}>
                      Unoptimized Jobs
                    </div>
                    <div style={{ fontSize: '18px', fontWeight: '700', color: '#2d3436' }}>
                      {jobs.length} jobs pending optimization
                    </div>
                  </div>
                </div>
              )}
              
              {/* Summary Section - Dependency Mode */}
              {mode === 'dependency' && (
                <div 
                  className="job-modal-summary" 
                  style={{
                    display: 'flex',
                    gap: '16px',
                    marginBottom: '20px',
                    padding: '16px',
                    background: 'linear-gradient(135deg, #fab1a0 0%, #ff7675 100%)',
                    borderRadius: '8px',
                    fontSize: '13px',
                    alignItems: 'center'
                  }}
                >
                  <span style={{ fontSize: '24px' }}>🔗</span>
                  <div>
                    <div style={{ fontSize: '11px', color: '#2d3436', marginBottom: '2px', opacity: 0.8 }}>
                      Dependency Relationships
                    </div>
                    <div style={{ fontSize: '18px', fontWeight: '700', color: '#2d3436' }}>
                      {jobs.length} job dependencies found
                    </div>
                  </div>
                </div>
              )}
              
              {/* Table Container */}
              <div 
                className="job-sequence-table-container" 
                style={{
                  overflowX: 'auto',
                  overflowY: 'auto',
                  maxHeight: '60vh',
                  borderRadius: '8px',
                  border: '1px solid #e1e8ed'
                }}
              >
                <table 
                  className="job-sequence-table" 
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '12px',
                    tableLayout: 'fixed'
                  }}
                >
                  <thead>
                    <tr style={{ 
                      background: '#667eea', 
                      color: 'white',
                      position: 'sticky',
                      top: 0,
                      zIndex: 10
                    }}>
                      <th style={{ 
                        padding: '12px 8px', 
                        textAlign: 'left', 
                        width: '50px',
                        fontWeight: '600'
                      }}>#</th>
                      <th style={{ 
                        padding: '12px 8px', 
                        textAlign: 'left', 
                        width: '150px',
                        fontWeight: '600'
                      }}>Job ID</th>
                      <th style={{ 
                        padding: '12px 8px', 
                        textAlign: 'left', 
                        width: '300px',
                        fontWeight: '600'
                      }}>Description</th>
                      
                      {mode === 'optimized' ? (
                        <>
                          <th style={{ padding: '12px 8px', textAlign: 'left', width: '100px', fontWeight: '600' }}>Start Time</th>
                          <th style={{ padding: '12px 8px', textAlign: 'left', width: '100px', fontWeight: '600' }}>End Time</th>
                          <th style={{ padding: '12px 8px', textAlign: 'center', width: '80px', fontWeight: '600' }}>Shift</th>
                          <th style={{ padding: '12px 8px', textAlign: 'left', width: '100px', fontWeight: '600' }}>Duration</th>
                          <th style={{ padding: '12px 8px', textAlign: 'left', width: '120px', fontWeight: '600' }}>Setup Time</th>
                          <th style={{ padding: '12px 8px', textAlign: 'left', width: '600px', fontWeight: '600' }}>Setup Reason</th>
                        </>
                      ) : mode === 'dependency' ? (
                        <>
                          <th style={{ padding: '12px 8px', textAlign: 'left', width: '150px', fontWeight: '600' }}>Depends On</th>
                          <th style={{ padding: '12px 8px', textAlign: 'left', width: '150px', fontWeight: '600' }}>End Date</th>
                          <th style={{ padding: '12px 8px', textAlign: 'center', width: '120px', fontWeight: '600' }}>Status</th>
                        </>
                      ) : (
                        <>
                          <th style={{ padding: '12px 8px', textAlign: 'left', width: '150px', fontWeight: '600' }}>End Date</th>
                          <th style={{ padding: '12px 8px', textAlign: 'left', minWidth: '200px', fontWeight: '600' }}>Dependencies</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map((job, index) => {
                      // Debug: Log the first job to see its structure
                      if (index === 0) {
                        console.log('First job object:', job);
                        console.log('Mode:', mode);
                        const excelKeys = Object.keys(job.parameters?.excelData || {});
                        console.log('ALL excelData keys:', excelKeys);
                        excelKeys.forEach(key => {
                          console.log(`  ${key}:`, job.parameters.excelData[key]);
                        });
                      }
                      
                      return (
                        <tr 
                          key={index} 
                          style={{
                            background: index % 2 === 0 ? '#f8f9fa' : 'white',
                            borderBottom: '1px solid #e0e0e0',
                            transition: 'background 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#e8eef7'}
                          onMouseLeave={(e) => e.currentTarget.style.background = index % 2 === 0 ? '#f8f9fa' : 'white'}
                        >
                          <td style={{ 
                            padding: '10px 8px',
                            fontWeight: '600',
                            color: '#7f8c8d'
                          }}>
                            {index + 1}
                          </td>
                          <td style={{ 
                            padding: '10px 8px', 
                            fontFamily: 'monospace',
                            fontSize: '11px',
                            fontWeight: '600',
                            color: '#2c3e50'
                          }} title={job.job_id || job.name}>
                            {job.job_id || job.name}
                          </td>
                          <td style={{
                            padding: '10px 8px',
                            maxWidth: '300px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: '#34495e'
                          }} title={job.description}>
                            {job.description || 'N/A'}
                          </td>
                          
                          {mode === 'optimized' ? (
                            <>
                              <td style={{ padding: '10px 8px', color: '#27ae60', fontWeight: '600' }}>
                                {formatTimeForDisplay(job.start_time)}
                              </td>
                              <td style={{ padding: '10px 8px', color: '#e74c3c', fontWeight: '600' }}>
                                {formatTimeForDisplay(job.end_time)}
                              </td>
                              <td style={{ 
                                padding: '10px 8px', 
                                textAlign: 'center',
                                fontWeight: '700',
                                color: '#667eea'
                              }}>
                                {job.shift_number}
                              </td>
                              <td style={{ padding: '10px 8px', fontWeight: '600', color: '#3498db' }}>
                                {calculateDuration(job.start_time, job.end_time)}
                              </td>
                              <td style={{ padding: '10px 8px', color: '#f39c12', fontWeight: '600' }}>
                                {formatTimeForDisplay(job.actual_setup_time || 0)}
                              </td>
                              <td style={{
                                padding: '10px 8px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                color: '#7f8c8d',
                                fontSize: '11px'
                              }} title={job.setup_reason || 'N/A'}>
                                {job.setup_reason || 'N/A'}
                              </td>
                            </>
                          ) : mode === 'dependency' ? (
                            <>
                              <td style={{ 
                                padding: '10px 8px', 
                                fontFamily: 'monospace',
                                fontSize: '11px',
                                fontWeight: '600',
                                color: '#e74c3c'
                              }} title={job.dependency_job_id}>
                                {job.dependency_job_id || 'N/A'}
                              </td>
                              <td style={{ padding: '10px 8px', color: '#7f8c8d' }}>
                                {formatDate(job.enddate || job.parameters?.excelData?.enddate)}
                              </td>
                              <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                                <span style={getStatusBadgeStyle(job.status || 'pending')}>
                                  {job.status || 'pending'}
                                </span>
                              </td>
                            </>
                          ) : (
                            <>
                              <td style={{ padding: '10px 8px', color: '#7f8c8d' }}>
                                {formatDate(job.enddate || job.parameters?.excelData?.enddate)}
                              </td>
                              <td style={{
                                padding: '10px 8px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                color: '#7f8c8d',
                                fontSize: '11px'
                              }} title={job.dependencies?.length > 0 ? job.dependencies.join(', ') : 'None'}>
                                {job.dependencies?.length > 0 ? job.dependencies.join(', ') : 'None'}
                              </td>
                            </>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div 
              className="no-jobs-message" 
              style={{
                textAlign: 'center',
                padding: '60px 40px',
                color: '#95a5a6'
              }}
            >
              <div style={{ fontSize: '64px', marginBottom: '16px' }}>📭</div>
              <p style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 8px 0', color: '#7f8c8d' }}>
                No jobs available
              </p>
              <p style={{ fontSize: '14px', margin: 0, color: '#95a5a6' }}>
                {mode === 'dependency' 
                  ? 'No dependency relationships found for this connection.'
                  : mode === 'optimized'
                  ? 'Run optimization to see scheduled jobs.'
                  : 'No jobs assigned to this machine yet.'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default JobsTableModal;